import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Button,
  Divider,
  Alert,
  TextField,
  Paper,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ImageIcon from '@mui/icons-material/Image';
import DownloadIcon from '@mui/icons-material/Download';
import Grid from '@mui/material/Grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/AuthProvider';
import { PdfPreview } from '@/components/PdfPreview';
import { ImageWithRegions } from '@/components/ImageWithRegions';
import { QuestionList } from '@/components/QuestionList';
import { pdfjs } from 'react-pdf';
import { PipelineProgress, type PipelineStatus } from '@/components/PipelineProgress';
import { PaperMapReviewer } from '@/components/PaperMapReviewer';
import { EssayResultsDisplay } from '@/components/EssayResultsDisplay';
import type { AssignmentStatus, QuestionResult, EssayGradingResult } from '@/types/grading';
import type { QuestionRubric, ExtractedQuestion, QuestionRegion } from '@/types/pipeline';
import { alpha } from '@mui/material/styles';
import { getQuestionRubricAccentColor, HIGHLIGHT_STROKE } from '@/lib/regionColors';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinearProgress from '@mui/material/LinearProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

interface PaperMapRegion {
  id: string;
  question_id: string;
  question_label: string | null;
  page_index: number;
  bbox_x: number;
  bbox_y: number;
  bbox_width: number;
  bbox_height: number;
  confidence: number;
}

interface GradingDraft {
  id: string;
  question_id: string;
  marks_awarded: number;
  max_marks: number;
  confidence: number;
  rationale: Array<{
    criterion: string;
    marksAwarded: number;
    maxMarks: number;
    evidence: string;
  }>;
  student_feedback: string;
  provider: string;
  model_version: string;
  created_at: string;
}

interface Assignment {
  id: string;
  student_name: string | null;
  student_class: string | null;
  grade_level: string;
  question_type: string | null;
  status: AssignmentStatus;
  submit_code: string | null;
  image_paths: string[];
  wizard_data: {
    questionResult?: unknown;
    extractedQuestions?: ExtractedQuestion[];
    rubrics?: QuestionRubric[];
  } | null;
  created_at: string;
}

interface SubmissionQuestionResult {
  questionId: string;
  marksAwarded: number;
  maxMarks: number;
  transcribedAnswer: string;
  feedback: string;
  confidence: number;
  rationale: { criterion: string; marksAwarded: number; maxMarks: number; evidence: string }[];
}

interface SubmissionGradingResult {
  questions: SubmissionQuestionResult[];
  totalMarks: number;
  maxTotalMarks: number;
  gradedAt: string;
  /** Present while a grading run is writing results incrementally */
  progress?: { completed: number; total: number };
  /** Grading-queue metadata (enqueued/claimed/attempts) — presence means the row is queue-managed */
  _queue?: { enqueuedAt?: string; claimedAt?: string; attempts?: number; lastError?: string };
  grading_error?: string;
}

interface StudentSubmission {
  id: string;
  student_name: string;
  student_class: string | null;
  image_paths: string[];
  status: 'pending' | 'grading' | 'graded';
  submitted_at: string;
  grading_result: SubmissionGradingResult | null;
  has_pending_review?: boolean;
}

interface GradingResult {
  id: string;
  provider: string;
  model_version: string;
  questions: QuestionResult[];
  total_score: number;
  max_total_score: number;
  overall_feedback: string;
  confidence: number;
  needs_review: boolean;
  review_reasons: string[] | null;
  teacher_adjusted: boolean;
  adjusted_score: number | null;
  adjusted_feedback: string | null;
  processing_time_ms: number | null;
  created_at: string;
}

const statusColors: Record<AssignmentStatus, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
  draft: 'default',
  ready_for_submission: 'info',
  pending: 'default',
  processing: 'primary',
  completed: 'success',
  failed: 'error',
  review: 'warning',
};

const statusLabels: Record<AssignmentStatus, string> = {
  draft: '草稿',
  ready_for_submission: '等待提交',
  pending: '等待中',
  processing: '批改中',
  completed: '已完成',
  failed: '失敗',
  review: '待覆核',
};

export function AssignmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [result, setResult] = useState<GradingResult | null>(null);
  const [pipelineStatus, setPipelineStatus] = useState<PipelineStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [adjustedScore, setAdjustedScore] = useState<string>('');
  const [adjustedFeedback, setAdjustedFeedback] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [paperMapId, setPaperMapId] = useState<string | null>(null);
  const [paperMapRegions, setPaperMapRegions] = useState<PaperMapRegion[]>([]);
  const [continuingPipeline, setContinuingPipeline] = useState(false);
  const [gradingDrafts, setGradingDrafts] = useState<GradingDraft[]>([]);
  const [essayResult, setEssayResult] = useState<EssayGradingResult | null>(null);
  const [showAssignmentInfo, setShowAssignmentInfo] = useState(false);
  const [submissions, setSubmissions] = useState<StudentSubmission[]>([]);
  const [gradingSubmission, setGradingSubmission] = useState<string | null>(null);
  const [expandedSubmission, setExpandedSubmission] = useState<string | null>(null);
  // ✅ 新增：覆核相關 state 和 helper
  const [confirmedQuestions, setConfirmedQuestions] = useState<Set<string>>(new Set());
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);

  const isQuestionFlagged = (q: SubmissionQuestionResult) => q.confidence < 0.7;

  const handleConfirmQuestion = (submissionId: string, questionId: string) => {
    setConfirmedQuestions((prev) => {
      const next = new Set(prev);
      next.add(`${submissionId}-${questionId}`);
      return next;
    });
  };
  // ✅ 新增結束

  const [detailPageIndex, setDetailPageIndex] = useState(0);
  const [detailFocusedId, setDetailFocusedId] = useState<string | null>(null);
  const [detailDetectionImages, setDetailDetectionImages] = useState<string[]>([]);
  const detailSplitContainerRef = useRef<HTMLDivElement | null>(null);
  const [detailLeftPaneWidth, setDetailLeftPaneWidth] = useState(420);
  const [detailSplitDragging, setDetailSplitDragging] = useState(false);

  useEffect(() => {
    if (!detailSplitDragging) return;

    const clampWidth = (clientX: number) => {
      const el = detailSplitContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const minLeft = 260;
      const minRight = 300;
      const gutter = 10;
      let next = clientX - rect.left;
      next = Math.max(minLeft, Math.min(next, rect.width - minRight - gutter));
      setDetailLeftPaneWidth(next);
    };

    const onMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      clampWidth(e.clientX);
    };
    const onMouseUp = () => setDetailSplitDragging(false);

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      e.preventDefault();
      clampWidth(t.clientX);
    };
    const onTouchEnd = () => setDetailSplitDragging(false);

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [detailSplitDragging]);

  const fetchPipelineStatus = useCallback(async () => {
    if (!id || !user) return;
    if (assignment?.status === 'draft' || assignment?.status === 'ready_for_submission') return;

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const response = await fetch(`/api/pipeline/status/${id}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPipelineStatus(data);

        // If awaiting paper map review, fetch the paper map data
        if (data.status === 'awaiting_paper_map_review') {
          await fetchPaperMapData();
        }
        
        // If pipeline is completed, fetch the grading drafts or essay result
        if (data.status === 'completed' || data.status === 'awaiting_grading_review') {
          await fetchGradingDrafts();
          await fetchEssayResult();
        }
      }
    } catch (err) {
      console.error('Failed to fetch pipeline status:', err);
    }
  }, [id, user]);

  const fetchPaperMapData = useCallback(async () => {
    if (!id) return;

    // Fetch paper map for this assignment
    const { data: paperMap } = await supabase
      .from('paper_maps')
      .select('id')
      .eq('assignment_id', id)
      .eq('is_current', true)
      .single();

    if (paperMap) {
      setPaperMapId(paperMap.id);

      // Fetch regions
      const { data: regions } = await supabase
        .from('paper_map_regions')
        .select('*')
        .eq('paper_map_id', paperMap.id)
        .order('page_index', { ascending: true })
        .order('bbox_y', { ascending: true });

      if (regions) {
        setPaperMapRegions(regions);
      }
    }
  }, [id]);

  const fetchGradingDrafts = useCallback(async () => {
    if (!id) return;

    const { data: drafts, error } = await supabase
      .from('grading_drafts')
      .select('*')
      .eq('assignment_id', id)
      .eq('is_current', true)
      .order('question_id', { ascending: true });

    if (error) {
      console.error('Failed to fetch grading drafts:', error);
      return;
    }

    if (drafts) {
      setGradingDrafts(drafts as GradingDraft[]);
    }
  }, [id]);

  const fetchEssayResult = useCallback(async () => {
    if (!id) return;

    const { data: essayData, error } = await supabase
      .from('essay_grading_results')
      .select('*')
      .eq('assignment_id', id)
      .eq('is_current', true)
      .maybeSingle();

    if (error) {
      console.error('Failed to fetch essay result:', error);
      return;
    }

    if (essayData) {
      // Map database fields to frontend types
      setEssayResult({
        id: essayData.id,
        assignmentId: essayData.assignment_id,
        pipelineRunId: essayData.pipeline_run_id,
        overallLevel: essayData.overall_level,
        totalScore: essayData.total_score,
        maxTotalScore: essayData.max_total_score,
        dimensions: essayData.dimensions,
        highlights: essayData.highlights || [],
        corrections: essayData.corrections || [],
        inlineAnnotations: essayData.inline_annotations || [],
        overallFeedback: essayData.overall_feedback,
        suggestedImprovements: essayData.suggested_improvements || [],
        confidence: essayData.confidence,
        provider: essayData.provider,
        modelVersion: essayData.model_version,
        processingTimeMs: essayData.processing_time_ms,
        transcribedText: essayData.transcribed_text,
        version: essayData.version,
        isCurrent: essayData.is_current,
        teacherAdjusted: essayData.teacher_adjusted,
        adjustedScore: essayData.adjusted_score,
        adjustedFeedback: essayData.adjusted_feedback,
        createdAt: essayData.created_at,
        updatedAt: essayData.updated_at,
      });
    }
  }, [id]);

  const handleContinuePipeline = async (editedRegions?: Array<{
    id: string;
    questionId: string;
    questionLabel?: string;
    pageIndex: number;
    bbox: { x: number; y: number; width: number; height: number };
    confidence: number;
  }>) => {
    if (!pipelineStatus?.id || !user) return;

    setContinuingPipeline(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      // If regions were edited, update them first
      if (editedRegions && paperMapId) {
        const updateResponse = await fetch(`/api/pipeline/paper-maps/${paperMapId}/regions`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({
            regions: editedRegions.map((r, index) => ({
              questionId: r.questionId,
              questionLabel: r.questionLabel,
              pageIndex: r.pageIndex,
              bbox: r.bbox,
              regionIndex: index,
            })),
          }),
        });

        if (!updateResponse.ok) {
          console.error('Failed to update regions:', await updateResponse.text());
          return;
        }
      }

      const response = await fetch(`/api/pipeline/continue/${pipelineStatus.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      if (response.ok) {
        // Clear paper map state and refresh
        setPaperMapId(null);
        setPaperMapRegions([]);
        await fetchPipelineStatus();
      }
    } catch (err) {
      console.error('Failed to continue pipeline:', err);
    } finally {
      setContinuingPipeline(false);
    }
  };

  const fetchData = useCallback(async () => {
    if (!user || !id) return;

    const { data: assignmentData } = await supabase
      .from('assignments')
      .select('*')
      .eq('id', id)
      .eq('teacher_id', user.id)
      .single();

    if (assignmentData) {
      setAssignment(assignmentData as Assignment);

      // Fetch student submissions
      if (['ready_for_submission', 'review', 'completed'].includes(assignmentData.status)) {
        const { data: subs } = await supabase
          .from('student_submissions')
          .select('*')
          .eq('assignment_id', id)
          .order('submitted_at', { ascending: false });
        if (subs) setSubmissions(subs as StudentSubmission[]);
      }
    }

    const { data: resultData } = await supabase
      .from('grading_results')
      .select('*')
      .eq('assignment_id', id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (resultData) {
      setResult(resultData as GradingResult);
      if (resultData.adjusted_score !== null) {
        setAdjustedScore(resultData.adjusted_score.toString());
      }
      if (resultData.adjusted_feedback) {
        setAdjustedFeedback(resultData.adjusted_feedback);
      }
    }

    // Fetch pipeline status and grading drafts
    await fetchPipelineStatus();
    await fetchGradingDrafts();
    await fetchEssayResult();

    setLoading(false);
  }, [user, id, navigate, fetchPipelineStatus, fetchGradingDrafts, fetchEssayResult]);

  useEffect(() => {
    fetchData();

    // Subscribe to real-time updates
    const channel = supabase
      .channel(`assignment:${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'assignments',
          filter: `id=eq.${id}`,
        },
        () => {
          fetchData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'grading_results',
          filter: `assignment_id=eq.${id}`,
        },
        () => {
          fetchData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pipeline_runs',
          filter: `assignment_id=eq.${id}`,
        },
        () => {
          fetchPipelineStatus();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'grading_drafts',
          filter: `assignment_id=eq.${id}`,
        },
        () => {
          fetchGradingDrafts();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'essay_grading_results',
          filter: `assignment_id=eq.${id}`,
        },
        () => {
          fetchEssayResult();
        }
      )
      .subscribe();

    // Poll for pipeline status while processing
    const pollInterval = setInterval(() => {
      if (assignment?.status === 'processing') {
        fetchPipelineStatus();
      }
    }, 2000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [id, fetchData, fetchPipelineStatus, fetchGradingDrafts, fetchEssayResult, assignment?.status]);

  // Regenerate detection page images from original image_paths
  useEffect(() => {
    if (!assignment?.image_paths?.length) return;
    if (!assignment.wizard_data?.questionResult) return;
    if (detailDetectionImages.length > 0) return;

    const MAX_DIM = 2048;

    const generateImages = async () => {
      const allImages: string[] = [];
      for (const path of assignment.image_paths) {
        const url = path.startsWith('http') ? path : supabase.storage.from('assignment-images').getPublicUrl(path).data.publicUrl;

        if (path.toLowerCase().endsWith('.pdf')) {
          try {
            const pdf = await pdfjs.getDocument(url).promise;
            for (let i = 1; i <= pdf.numPages; i++) {
              const page = await pdf.getPage(i);
              const viewport = page.getViewport({ scale: 1 });
              const scale = Math.min(MAX_DIM / viewport.width, MAX_DIM / viewport.height, 2);
              const sv = page.getViewport({ scale });
              const canvas = document.createElement('canvas');
              canvas.width = sv.width;
              canvas.height = sv.height;
              const ctx = canvas.getContext('2d')!;
              await page.render({ canvasContext: ctx, viewport: sv, canvas } as any).promise;
              allImages.push(canvas.toDataURL('image/jpeg', 0.85));
            }
          } catch (err) {
            console.error('Failed to render PDF for detection overlay:', err);
          }
        } else {
          try {
            const resp = await fetch(url);
            const blob = await resp.blob();
            const dataUrl = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.readAsDataURL(blob);
            });
            allImages.push(dataUrl);
          } catch (err) {
            console.error('Failed to load image for detection overlay:', err);
          }
        }
      }
      if (allImages.length > 0) {
        setDetailDetectionImages(allImages);
      }
    };

    generateImages();
  }, [assignment?.image_paths, assignment?.wizard_data?.questionResult, detailDetectionImages.length]);

  const handleGradeSubmission = async (submissionId: string) => {
    if (!id || !user || gradingSubmission) return;

    // Re-grading throws away the current AI result and any teacher overrides on
    // it, and costs another full grading run — worth a deliberate confirmation.
    const existing = submissions.find((s) => s.id === submissionId);
    if (existing?.status === 'graded') {
      const reviewed = existing.grading_result?.questions?.some((q: any) => q.reviewed_at);
      const warning = reviewed
        ? `重新批改會清除現有 AI 批改結果，以及老師已審核的分數與評語。確定要重新批改 ${existing.student_name} 的作業嗎？`
        : `重新批改會清除現有批改結果並重新執行一次。確定要重新批改 ${existing.student_name} 的作業嗎？`;
      if (!window.confirm(warning)) return;
    }

    setGradingSubmission(submissionId);
    try {
      const response = await fetch(`/api/assignments/${id}/submissions/${submissionId}/grade`, {
        method: 'POST',
        headers: { 'x-teacher-id': user.id },
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '開始批改失敗');
      }
      // Optimistically update status in the list
      setSubmissions((prev) =>
        prev.map((s) => (s.id === submissionId ? { ...s, status: 'grading' } : s)),
      );
    } catch (err) {
      console.error('Failed to start grading:', err);
    } finally {
      setGradingSubmission(null);
    }
  };

  const handleGradeAll = async () => {
    if (!id || !user) return;
    try {
      const response = await fetch(`/api/assignments/${id}/submissions/grade-all`, {
        method: 'POST',
        headers: { 'x-teacher-id': user.id },
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '批改失敗');
      }
      // Refresh so the queue chips appear immediately
      const { data } = await supabase
        .from('student_submissions')
        .select('*')
        .eq('assignment_id', id)
        .order('submitted_at', { ascending: false });
      if (data) setSubmissions(data as StudentSubmission[]);
    } catch (err) {
      console.error('Failed to bulk grade:', err);
    }
  };

  // Poll for submission status while any are grading or waiting in the queue
  useEffect(() => {
    const hasActive = submissions.some(
      (s) => s.status === 'grading' || (s.status === 'pending' && s.grading_result?._queue),
    );
    if (!hasActive || !id) return;

    const interval = setInterval(async () => {
      const { data } = await supabase
        .from('student_submissions')
        .select('*')
        .eq('assignment_id', id)
        .order('submitted_at', { ascending: false });
      if (data) setSubmissions(data as StudentSubmission[]);
    }, 2000);

    return () => clearInterval(interval);
  }, [submissions, id]);

  const handleRetryPipeline = async () => {
    if (!id || !user) return;

    setPipelineLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const response = await fetch('/api/pipeline/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ assignmentId: id }),
      });

      if (response.ok) {
        // Refresh data
        await fetchData();
      }
    } catch (err) {
      console.error('Failed to retry pipeline:', err);
    } finally {
      setPipelineLoading(false);
    }
  };

  const handleSaveAdjustment = async () => {
    if (!result || !user) return;

    setSaving(true);
    const { error } = await supabase
      .from('grading_results')
      .update({
        teacher_adjusted: true,
        adjusted_score: adjustedScore ? parseInt(adjustedScore, 10) : null,
        adjusted_feedback: adjustedFeedback || null,
        reviewed_at: new Date().toISOString(),
        reviewed_by: user.id,
      })
      .eq('id', result.id);

    if (!error && assignment) {
      await supabase
        .from('assignments')
        .update({ status: 'completed' })
        .eq('id', assignment.id);
    }

    setSaving(false);
  };

  const handleGoToReview = () => {
    navigate(`/review/${id}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!assignment) {
    return (
      <Box>
        <Alert severity="error">找不到作業記錄</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/assignments')} sx={{ mt: 2 }}>
          返回列表
        </Button>
      </Box>
    );
  }

  const getImageUrl = (path: string) => {
    if (path.startsWith('http')) return path;
    const { data } = supabase.storage.from('assignment-images').getPublicUrl(path);
    return data.publicUrl;
  };

  const downloadOriginalFile = async (path: string) => {
    const url = getImageUrl(path);
    const rawName = path.split('/').pop()?.split('?')[0] || 'file';
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = rawName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download failed:', err);
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const isEssay = assignment.question_type === 'essay';
  
  // For essays, check essayResult; for regular assignments, check result
  const hasGradingResult = isEssay ? !!essayResult : !!result;
  
  const showPipelineProgress = (assignment.status === 'processing' || 
    (assignment.status === 'failed' && pipelineStatus) ||
    (pipelineStatus && !hasGradingResult)) && 
    pipelineStatus?.status !== 'awaiting_paper_map_review';
  
  const showPaperMapReviewer = pipelineStatus?.status === 'awaiting_paper_map_review' && 
    paperMapId && 
    paperMapRegions.length > 0;

  const showEssayResults = isEssay && essayResult && !showPipelineProgress && !showPaperMapReviewer;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/assignments')} sx={{ mb: 2 }}>
        返回列表
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" component="h1">
          作業詳情
        </Typography>
        <Chip
          label={statusLabels[assignment.status]}
          color={statusColors[assignment.status] as any}
        />
        {assignment.status === 'review' && (
          <Button
            variant="contained"
            color="warning"
            startIcon={<RateReviewIcon />}
            onClick={handleGoToReview}
          >
            前往審核
          </Button>
        )}
      </Box>

      {/* Submission info + rubric for active assignments */}
      {['ready_for_submission', 'review', 'completed'].includes(assignment.status) && assignment.submit_code && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>學生提交</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">提交碼</Typography>
                  <Typography variant="h4" fontWeight={700} letterSpacing={4} color="primary.main">
                    {assignment.submit_code}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">提交連結</Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {window.location.origin}/submit
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigator.clipboard.writeText(assignment.submit_code!)}
                >
                  複製提交碼
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">
                  已收到 {submissions.length} 份提交
                </Typography>
                {submissions.some((s) => s.status === 'pending' && !s.grading_result?._queue) && (
                  <Button size="small" variant="contained" onClick={handleGradeAll}>
                    全部批改（
                    {submissions.filter((s) => s.status === 'pending' && !s.grading_result?._queue).length} 份）
                  </Button>
                )}
                {submissions.some((s) => s.has_pending_review) && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<RateReviewIcon />}
                    onClick={() => navigate(`/assignments/${id}/pending-reviews`)}
                  >
                    查看所有待審核（
                    {submissions.filter((s) => s.has_pending_review).length} 份）
                  </Button>
                )}
              </Stack>
              {submissions.length > 0 ? (
                <Stack spacing={0}>
                  {submissions.map((sub) => {
                    const gr = sub.grading_result;
                    const isExpanded = expandedSubmission === sub.id;

                    return (
                      <Paper key={sub.id} variant="outlined" sx={{ mb: 1, overflow: 'hidden' }}>
                        {/* Row */}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 84px 108px auto auto 40px',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Box>
                            <Typography fontWeight={600}>{sub.student_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {sub.student_class || '-'} · {new Date(sub.submitted_at).toLocaleString('zh-HK')}
                            </Typography>
                          </Box>

                          {/* Score chip — only once the final totals exist (not during a partial run) */}
                          {gr && typeof gr.totalMarks === 'number' ? (
                            <Chip
                              label={`${gr.totalMarks} / ${gr.maxTotalMarks}`}
                              color={
                                gr.totalMarks / gr.maxTotalMarks >= 0.75
                                  ? 'success'
                                  : gr.totalMarks / gr.maxTotalMarks >= 0.5
                                  ? 'warning'
                                  : 'error'
                              }
                              size="small"
                              variant="filled"
                            />
                          ) : (
                            <Box />
                          )}

                          <Chip
                            size="small"
                            label={
                              sub.status === 'pending'
                                ? sub.grading_result?._queue
                                  ? '排隊中…'
                                  : '待批改'
                                : sub.status === 'grading'
                                ? sub.grading_result?.progress
                                  ? `批改中 ${sub.grading_result.progress.completed}/${sub.grading_result.progress.total}`
                                  : '批改中…'
                                : '已批改'
                            }
                            color={
                              sub.status === 'pending'
                                ? sub.grading_result?._queue
                                  ? 'primary'
                                  : 'default'
                                : sub.status === 'grading'
                                ? 'primary'
                                : 'success'
                            }
                          />

                          {/* Review is the frequent action on a graded row, so it takes the
                              primary button. Re-grading is rare and destructive — it discards
                              the AI result and any teacher overrides — so it is demoted to a
                              quiet text button and asks for confirmation. */}
                          {/* Always rendered, disabled when there is nothing outstanding —
                              letting it vanish made the action column jump between rows. */}
                          <Tooltip
                            title={
                              sub.status !== 'graded'
                                ? '批改完成後才能審核'
                                : sub.has_pending_review
                                  ? '審核 AI 標記為需要人手確認的題目'
                                  : '已全部審核完成，沒有待審核的題目'
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<RateReviewIcon />}
                                disabled={!sub.has_pending_review}
                                onClick={() => navigate(`/assignments/${id}/submissions/${sub.id}/review`)}
                                sx={{ minWidth: 92 }}
                              >
                                審核
                              </Button>
                            </span>
                          </Tooltip>
                          <Button
                            size="small"
                            variant={sub.status === 'graded' ? 'text' : 'contained'}
                            color={sub.status === 'graded' ? 'inherit' : 'primary'}
                            disabled={
                              sub.status === 'grading' ||
                              (sub.status === 'pending' && !!sub.grading_result?._queue) ||
                              gradingSubmission === sub.id
                            }
                            onClick={() => handleGradeSubmission(sub.id)}
                            startIcon={
                              (gradingSubmission === sub.id || sub.status === 'grading') ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : null
                            }
                            sx={{ minWidth: 96, ...(sub.status === 'graded' ? { color: 'text.secondary', fontWeight: 400 } : {}) }}
                          >
                            {sub.status === 'graded' ? '重新批改' : '開始批改'}
                          </Button>

                          {gr ? (
                            <IconButton
                              size="small"
                              onClick={() => setExpandedSubmission(isExpanded ? null : sub.id)}
                              title={isExpanded ? '收起' : '查看詳情'}
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          ) : (
                            <Box />
                          )}
                        </Box>

                        {/* Expandable grading details */}
                        {/* ✅ 以下整段是改動 */}
                        {gr && (
                          <Collapse in={isExpanded}>
                            <Divider />
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>

                              {/* ✅ 覆核進度條 + Filter toggle */}
                              {(() => {
                                const flagged = gr.questions.filter(isQuestionFlagged);
                                const confirmed = flagged.filter(
                                  (q) => confirmedQuestions.has(`${sub.id}-${q.questionId}`)
                                );
                                if (flagged.length === 0) return null;
                                return (
                                  <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                      <Typography variant="subtitle2">
                                        覆核進度：{confirmed.length} / {flagged.length} 題
                                      </Typography>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            size="small"
                                            checked={showFlaggedOnly}
                                            onChange={(e) => setShowFlaggedOnly(e.target.checked)}
                                          />
                                        }
                                        label={<Typography variant="caption">只顯示需覆核</Typography>}
                                      />
                                    </Box>
                                    <LinearProgress
                                      variant="determinate"
                                      value={(confirmed.length / flagged.length) * 100}
                                      sx={{ borderRadius: 1, height: 6 }}
                                    />
                                  </Box>
                                );
                              })()}

                              <Typography variant="subtitle2" gutterBottom>
                                各題批改結果
                              </Typography>

                              <Stack spacing={1}>
                                {/* ✅ 加了 filter + flag 邏輯 */}
                                {gr.questions
                                  .filter((q) => !showFlaggedOnly || isQuestionFlagged(q))
                                  .map((q) => {
                                    const flagged = isQuestionFlagged(q);
                                    const confirmKey = `${sub.id}-${q.questionId}`;
                                    const isConfirmed = confirmedQuestions.has(confirmKey);

                                    return (
                                      <Paper
                                        key={q.questionId}
                                        variant="outlined"
                                        sx={{
                                          p: 1.5,
                                          // ✅ 左邊框顏色根據 flag 狀態
                                          borderLeft: flagged
                                            ? `4px solid ${isConfirmed ? '#4caf50' : '#ff9800'}`
                                            : '4px solid #2196f3',
                                        }}
                                      >
                                        {/* ✅ Flag 警告列 */}
                                        {flagged && !isConfirmed && (
                                          <Alert
                                            severity="warning"
                                            icon={<WarningAmberIcon fontSize="small" />}
                                            sx={{ mb: 1, py: 0 }}
                                            action={
                                              <Button
                                                color="inherit"
                                                size="small"
                                                onClick={() => handleConfirmQuestion(sub.id, q.questionId)}
                                              >
                                                確認無誤
                                              </Button>
                                            }
                                          >
                                            信心度偏低（{(q.confidence * 100).toFixed(0)}%），建議覆核
                                          </Alert>
                                        )}

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                          {/* ✅ 左側加了狀態 badge */}
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                              {q.questionId}
                                            </Typography>
                                            {flagged && isConfirmed && (
                                              <Chip
                                                icon={<CheckCircleIcon />}
                                                label="已確認"
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                              />
                                            )}

                                          </Box>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                              size="small"
                                              label={`${(q.confidence * 100).toFixed(0)}%`}
                                              color={q.confidence >= 0.7 ? 'success' : 'warning'}
                                              variant="outlined"
                                            />
                                            <Typography variant="body2" fontWeight={700} color="primary">
                                              {q.marksAwarded} / {q.maxMarks}
                                            </Typography>
                                          </Box>
                                        </Box>

                                        {/* 以下和原本一樣，沒改 */}
                                        {q.transcribedAnswer && (
                                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                            學生答案：{q.transcribedAnswer}
                                          </Typography>
                                        )}
                                        {q.rationale?.length > 0 && (
                                          <Box sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider', mb: 0.5 }}>
                                            {q.rationale.map((r, i) => (
                                              <Typography key={i} variant="caption" display="block" color="text.secondary">
                                                [{r.criterion}] {r.marksAwarded}/{r.maxMarks} – {r.evidence}
                                              </Typography>
                                            ))}
                                          </Box>
                                        )}
                                        <Typography variant="body2">
                                          <strong>評語：</strong>{q.feedback}
                                        </Typography>
                                      </Paper>
                                    );
                                  })}
                              </Stack>

                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                批改於 {new Date(gr.gradedAt).toLocaleString('zh-HK')}
                              </Typography>
                            </Box>
                          </Collapse>
                        )}
                        {/* ✅ 改動結束 */}
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  尚未收到任何學生提交。請將提交碼分享給學生。
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Side-by-side: test paper with region overlay + rubric */}
          {(() => {
            const rubrics = assignment.wizard_data?.rubrics || [];
            const questions = assignment.wizard_data?.extractedQuestions || [];
            const allRegions = (assignment.wizard_data?.questionResult as any)?.regions || [];
            const pageRegions = allRegions.filter((r: any) => (r.pageIndex ?? 0) === detailPageIndex);
            const highlightIdx = detailFocusedId != null
              ? pageRegions.findIndex((r: any) => r.questionId === detailFocusedId)
              : undefined;

            if (rubrics.length === 0 && questions.length === 0) return null;

            return (
              <Box
                ref={detailSplitContainerRef}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexDirection: { xs: 'column', lg: 'row' },
                  mb: 3,
                  width: '100%',
                }}
              >
                {/* Left: test paper with region overlay */}
                <Box
                  sx={{
                    flex: 'none',
                    width: { xs: '100%', lg: `${detailLeftPaneWidth}px` },
                    minWidth: { lg: 260 },
                    maxWidth: { lg: '100%' },
                    position: 'sticky',
                    top: 16,
                  }}
                >
                  {assignment.image_paths.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      {assignment.image_paths.map((path, index) => (
                        <Button
                          key={`dl-${path}-${index}`}
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => void downloadOriginalFile(path)}
                        >
                          {assignment.image_paths.length === 1 ? '下載試卷原檔' : `下載檔案 ${index + 1}`}
                        </Button>
                      ))}
                    </Box>
                  )}
                  {detailDetectionImages.length > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <IconButton size="small" disabled={detailPageIndex <= 0} onClick={() => setDetailPageIndex((p) => p - 1)}>
                        <ExpandLessIcon fontSize="small" sx={{ transform: 'rotate(-90deg)' }} />
                      </IconButton>
                      <Typography variant="body2" color="text.secondary">
                        第 {detailPageIndex + 1} / {detailDetectionImages.length} 頁
                      </Typography>
                      <IconButton size="small" disabled={detailPageIndex >= detailDetectionImages.length - 1} onClick={() => setDetailPageIndex((p) => p + 1)}>
                        <ExpandMoreIcon fontSize="small" sx={{ transform: 'rotate(-90deg)' }} />
                      </IconButton>
                    </Box>
                  )}
                  {detailDetectionImages[detailPageIndex] ? (
                    <ImageWithRegions
                      src={detailDetectionImages[detailPageIndex]}
                      regions={pageRegions}
                      highlightIndex={highlightIdx !== -1 ? highlightIdx : undefined}
                      onRegionHoverEnter={(questionId) => {
                        setDetailFocusedId(questionId);
                      }}
                      onRegionHoverLeave={() => {
                        setDetailFocusedId(null);
                      }}
                    />
                  ) : (
                    <Card>
                      <CardContent>
                        {assignment.image_paths.map((path, idx) => {
                          const url = getImageUrl(path);
                          const isPdf = path.toLowerCase().endsWith('.pdf');
                          if (isPdf) return <PdfPreview key={idx} url={url} width={Math.max(280, detailLeftPaneWidth - 32)} />;
                          return <Box key={idx} component="img" src={url} sx={{ maxWidth: '100%', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />;
                        })}
                      </CardContent>
                    </Card>
                  )}
                </Box>

                {/* Resizable split gutter (desktop) */}
                <Box
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="調整試卷與評分標準區域寬度"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setDetailSplitDragging(true);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    setDetailSplitDragging(true);
                  }}
                  sx={{
                    display: { xs: 'none', lg: 'flex' },
                    flex: 'none',
                    width: 10,
                    flexShrink: 0,
                    alignSelf: 'stretch',
                    minHeight: 120,
                    cursor: 'col-resize',
                    alignItems: 'center',
                    justifyContent: 'center',
                    touchAction: 'none',
                    userSelect: 'none',
                    '&:hover .detail-split-grip': { bgcolor: 'primary.main' },
                    ...(detailSplitDragging && { '& .detail-split-grip': { bgcolor: 'primary.main' } }),
                  }}
                >
                  <Box
                    className="detail-split-grip"
                    sx={{
                      width: 4,
                      height: 56,
                      borderRadius: 1,
                      bgcolor: 'divider',
                      transition: 'background-color 0.15s',
                    }}
                  />
                </Box>

                {/* Right: rubric */}
                <Card sx={{ flex: 1, minWidth: 0, width: { xs: '100%', lg: 'auto' } }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      評分標準（{rubrics.length} 題，共 {rubrics.reduce((s, r) => s + r.maxMarks, 0)} 分）
                    </Typography>
                    <QuestionList
                      questions={rubrics.map((r) => {
                        const q = questions.find((q) => q.questionId === r.questionId);
                        return {
                          questionId: r.questionId,
                          questionText: q?.questionText || '',
                          questionType: q?.questionType || 'unknown',
                          studentAnswer: q?.studentAnswer || '',
                          sectionInstruction: q?.sectionInstruction || '',
                        } as ExtractedQuestion;
                      })}
                      allRegions={allRegions as QuestionRegion[]}
                      focusedQuestionId={detailFocusedId}
                      onQuestionHoverEnter={(questionId) => {
                        setDetailFocusedId(questionId);
                        const regionsTyped = allRegions as QuestionRegion[];
                        const region =
                          regionsTyped.find(
                            (r) =>
                              r.questionId === questionId &&
                              (r.type === 'question' || r.type === 'mc-question')
                          ) ||
                          regionsTyped.find(
                            (r) =>
                              r.parentQuestion === questionId &&
                              (r.type?.startsWith('answer-') ?? false)
                          );
                        if (region && (region.pageIndex ?? 0) !== detailPageIndex) {
                          setDetailPageIndex(region.pageIndex ?? 0);
                        }
                      }}
                      onQuestionHoverLeave={() => setDetailFocusedId(null)}
                      renderQuestionContent={(question, isFocused, boxAccent) => {
                        const rubric = rubrics.find((r) => r.questionId === question.questionId);
                        if (!rubric) return null;
                        const strokeColor = isFocused ? HIGHLIGHT_STROKE : boxAccent;
                        return (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={700}
                                  sx={{
                                    color: isFocused
                                      ? HIGHLIGHT_STROKE
                                      : boxAccent ?? 'primary.main',
                                  }}
                                >
                                  {rubric.questionId}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={rubric.gradingMode === 'auto' ? '自動' : '評分標準'}
                                  variant="outlined"
                                  color={rubric.gradingMode === 'auto' ? 'info' : 'secondary'}
                                  sx={
                                    boxAccent || isFocused
                                      ? {
                                          borderColor: isFocused ? HIGHLIGHT_STROKE : boxAccent!,
                                          color: isFocused ? HIGHLIGHT_STROKE : boxAccent!,
                                        }
                                      : undefined
                                  }
                                />
                              </Box>
                              <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                sx={{ color: strokeColor ?? undefined }}
                              >
                                {rubric.maxMarks} 分
                              </Typography>
                            </Box>
                            {question.questionText && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {question.questionText}
                              </Typography>
                            )}
                            {rubric.modelAnswer && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                參考答案：{rubric.modelAnswer}
                              </Typography>
                            )}
                            {rubric.gradingMode === 'rubric' && rubric.criteria && rubric.criteria.length > 0 && (
                              <Box
                                sx={{
                                  mt: 0.5,
                                  pl: 1,
                                  borderLeft: '2px solid',
                                  borderColor: boxAccent ?? 'secondary.light',
                                }}
                              >
                                {rubric.criteria.map((c, ci) => (
                                  <Typography key={c.id} variant="caption" display="block" color="text.secondary">
                                    {ci + 1}. {c.description}（{c.points} 分）
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </>
                        );
                      }}
                    />
                  </CardContent>
                </Card>
              </Box>
            );
          })()}
        </>
      )}

      {/* Essay-specific full-width layout */}
      {assignment.status === 'ready_for_submission' ? null : showEssayResults ? (
        <Box>
          {/* Compact header with assignment info */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Typography>
                    <strong>學生：</strong> {assignment.student_name || '未命名'}
                  </Typography>
                  <Typography>
                    <strong>班級：</strong> {assignment.student_class || '-'}
                  </Typography>
                  <Typography>
                    <strong>年級：</strong> {assignment.grade_level}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<ImageIcon />}
                    onClick={() => setShowAssignmentInfo(!showAssignmentInfo)}
                    endIcon={showAssignmentInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  >
                    查看原稿
                  </Button>
                </Box>
              </Box>
              <Collapse in={showAssignmentInfo}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {assignment.image_paths.map((path, index) => {
                    const url = getImageUrl(path);
                    const isPdf = path.toLowerCase().endsWith('.pdf');
                    if (isPdf) {
                      return (
                        <Box key={index}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => void downloadOriginalFile(path)}
                            sx={{ mb: 1 }}
                          >
                            {assignment.image_paths.length === 1 ? '下載 PDF 原檔' : `下載 PDF ${index + 1}`}
                          </Button>
                          <PdfPreview url={url} />
                        </Box>
                      );
                    }
                    return (
                      <Box key={index}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => void downloadOriginalFile(path)}
                          sx={{ mb: 1 }}
                        >
                          {assignment.image_paths.length === 1 ? '下載圖片原檔' : `下載圖片 ${index + 1}`}
                        </Button>
                        <Box
                          component="img"
                          src={url}
                          alt={`作業圖片 ${index + 1}`}
                          sx={{
                            height: 200,
                            objectFit: 'contain',
                            borderRadius: 1,
                            cursor: 'pointer',
                            border: '1px solid #e0e0e0',
                          }}
                          onClick={() => window.open(url, '_blank')}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </CardContent>
          </Card>

          {/* Essay results - full width, no tabs */}
          <EssayResultsDisplay 
            result={essayResult} 
            imageUrls={assignment.image_paths.map(getImageUrl)}
          />
        </Box>
      ) : (
        /* Standard 2-column layout for non-essays */
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  作業資料
                </Typography>
                <Typography>
                  <strong>學生：</strong> {assignment.student_name || '未命名'}
                </Typography>
                <Typography>
                  <strong>班級：</strong> {assignment.student_class || '-'}
                </Typography>
                <Typography>
                  <strong>年級：</strong> {assignment.grade_level}
                </Typography>
                <Typography>
                  <strong>日期：</strong>{' '}
                  {new Date(assignment.created_at).toLocaleString('zh-HK')}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="h6">作業圖片</Typography>
                  {assignment.image_paths.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {assignment.image_paths.map((path, index) => (
                        <Button
                          key={`std-dl-${path}-${index}`}
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => void downloadOriginalFile(path)}
                        >
                          {assignment.image_paths.length === 1 ? '下載原檔' : `下載檔案 ${index + 1}`}
                        </Button>
                      ))}
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {assignment.image_paths.map((path, index) => {
                    const url = getImageUrl(path);
                    const isPdf = path.toLowerCase().endsWith('.pdf');
                    if (isPdf) {
                      return <PdfPreview key={index} url={url} width={400} />;
                    }
                    return (
                      <Box
                        key={index}
                        component="img"
                        src={url}
                        alt={`作業圖片 ${index + 1}`}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 400,
                          objectFit: 'contain',
                          borderRadius: 1,
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(url, '_blank')}
                      />
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
          {/* Pipeline Progress */}
          {showPipelineProgress && (
            <PipelineProgress
              pipelineStatus={pipelineStatus}
              loading={pipelineLoading}
              onRetry={handleRetryPipeline}
            />
          )}

          {/* Paper Map Review UI */}
          {showPaperMapReviewer && (
            <PaperMapReviewer
              assignmentId={assignment.id}
              paperMapId={paperMapId!}
              regions={paperMapRegions.map((r) => ({
                id: r.id,
                questionId: r.question_id,
                questionLabel: r.question_label || undefined,
                pageIndex: r.page_index,
                bbox: {
                  x: r.bbox_x,
                  y: r.bbox_y,
                  width: r.bbox_width,
                  height: r.bbox_height,
                },
                confidence: r.confidence,
              }))}
              imageUrls={assignment.image_paths.map(getImageUrl)}
              onConfirm={handleContinuePipeline}
              confirming={continuingPipeline}
            />
          )}

          {/* Legacy failed state (no pipeline status) */}
          {assignment.status === 'failed' && !pipelineStatus && (
            <Alert 
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={handleRetryPipeline}>
                  重試
                </Button>
              }
            >
              批改失敗，請重試
            </Alert>
          )}

          {/* Grading Results from new pipeline */}
          {gradingDrafts.length > 0 && (
            <Card sx={{ mt: showPipelineProgress ? 3 : 0 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">批改結果</Typography>
                  <Chip
                    label={`共 ${gradingDrafts.length} 題`}
                    color="primary"
                    size="small"
                  />
                </Box>

                {/* Total Score Summary */}
                <Paper sx={{ p: 2, bgcolor: 'primary.50', mb: 2 }}>
                  <Typography variant="h3" textAlign="center" color="primary">
                    {gradingDrafts.reduce((sum, d) => sum + d.marks_awarded, 0).toFixed(1)}
                    {' / '}
                    {gradingDrafts.reduce((sum, d) => sum + d.max_marks, 0).toFixed(1)}
                  </Typography>
                  <Typography variant="body2" textAlign="center" color="text.secondary">
                    平均信心度: {(gradingDrafts.reduce((sum, d) => sum + d.confidence, 0) / gradingDrafts.length * 100).toFixed(0)}%
                  </Typography>
                </Paper>

                {/* Check if any questions need review */}
                {gradingDrafts.some(d => d.confidence < 0.7) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    部分題目信心度較低，建議人工覆核
                  </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  各題評分
                </Typography>
                
                {gradingDrafts.map((draft) => (
                  <Paper key={draft.id} sx={{ p: 2, mb: 1, border: draft.confidence < 0.7 ? '1px solid' : 'none', borderColor: 'warning.main' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight="bold">題目 {draft.question_id}</Typography>
                        <Chip
                          label={`${(draft.confidence * 100).toFixed(0)}%`}
                          color={draft.confidence >= 0.7 ? 'success' : 'warning'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Typography fontWeight="bold" color="primary">
                        {draft.marks_awarded} / {draft.max_marks}
                      </Typography>
                    </Box>
                    
                    {/* Rationale breakdown */}
                    {draft.rationale && Array.isArray(draft.rationale) && draft.rationale.length > 0 && (
                      <Box sx={{ mb: 1, pl: 2, borderLeft: '2px solid', borderColor: 'grey.300' }}>
                        {draft.rationale.map((r, idx) => (
                          <Box key={idx} sx={{ mb: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>{r.criterion}:</strong> {r.marksAwarded}/{r.maxMarks} - {r.evidence}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>評語：</strong>{draft.student_feedback}
                    </Typography>
                  </Paper>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Essay Grading Results */}
          {essayResult && assignment.question_type === 'essay' && (
            <Card sx={{ mt: showPipelineProgress ? 3 : 0 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  作文批改結果
                </Typography>
                <EssayResultsDisplay 
                  result={essayResult} 
                  imageUrls={assignment.image_paths.map(getImageUrl)}
                />
              </CardContent>
            </Card>
          )}

          {/* Grading Results (legacy flow) */}
          {result && (
            <Card sx={{ mt: showPipelineProgress ? 3 : 0 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">批改結果</Typography>
                  <Chip
                    label={`信心度: ${(result.confidence * 100).toFixed(0)}%`}
                    color={result.confidence >= 0.7 ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>

                <Paper sx={{ p: 2, bgcolor: 'primary.50', mb: 2 }}>
                  <Typography variant="h3" textAlign="center" color="primary">
                    {result.teacher_adjusted && result.adjusted_score !== null
                      ? result.adjusted_score
                      : result.total_score}{' '}
                    / {result.max_total_score}
                  </Typography>
                </Paper>

                {result.needs_review && !result.teacher_adjusted && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    此結果需要人工覆核
                    {result.review_reasons && (
                      <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                        {result.review_reasons.map((reason, i) => (
                          <li key={i}>{reason}</li>
                        ))}
                      </ul>
                    )}
                  </Alert>
                )}

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  整體評語
                </Typography>
                <Typography paragraph>
                  {result.teacher_adjusted && result.adjusted_feedback
                    ? result.adjusted_feedback
                    : result.overall_feedback}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  各題評分
                </Typography>
                {(result.questions as QuestionResult[]).map((q, index) => (
                  <Paper key={index} sx={{ p: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography fontWeight="bold">題目 {q.questionNumber}</Typography>
                      <Typography>
                        {q.score} / {q.maxScore}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      答案：{q.extractedAnswer}
                    </Typography>
                    <Typography variant="body2">{q.feedback}</Typography>
                  </Paper>
                ))}

                {result.needs_review && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      教師調整
                    </Typography>
                    <TextField
                      fullWidth
                      label="調整分數"
                      type="number"
                      value={adjustedScore}
                      onChange={(e) => setAdjustedScore(e.target.value)}
                      sx={{ mb: 2 }}
                      inputProps={{ min: 0, max: result.max_total_score }}
                    />
                    <TextField
                      fullWidth
                      label="調整評語"
                      multiline
                      rows={3}
                      value={adjustedFeedback}
                      onChange={(e) => setAdjustedFeedback(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSaveAdjustment}
                      disabled={saving}
                    >
                      {saving ? <CircularProgress size={24} /> : '儲存調整'}
                    </Button>
                  </>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  使用 {result.provider} ({result.model_version})，
                  處理時間：{result.processing_time_ms ? `${(result.processing_time_ms / 1000).toFixed(1)}秒` : '-'}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Pending state - show start button */}
          {assignment.status === 'pending' && !pipelineStatus && (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" gutterBottom>
                  準備開始批改
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  點擊下方按鈕開始 AI 批改流程
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleRetryPipeline}
                  disabled={pipelineLoading}
                >
                  {pipelineLoading ? <CircularProgress size={24} /> : '開始批改'}
                </Button>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
      )}
    </Box>
  );
}
